// src/pages/AdminAttendanceTodayPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

// ✅ Only these options (no Present)
const STATUS_LIST = [
  { key: "SICK", label: "Sick" },
  { key: "NO_CALL_NO_SHOW", label: "NCNS" },
  { key: "LATE", label: "Late" },
  { key: "EMERGENCY", label: "Emergency" },
  { key: "OTHER", label: "Other" },
];

/* ----------------- date helpers ----------------- */
function parseYMD(s) {
  if (!s || typeof s !== "string") return null;
  const [y, m, d] = s.split("-").map((x) => Number(x));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d); // local
}
function diffDaysLocal(a, b) {
  const ms = 24 * 60 * 60 * 1000;
  const aa = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bb = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((aa.getTime() - bb.getTime()) / ms);
}
function getTodayDayOfWeekForWeek(week) {
  const today = new Date();
  const start = parseYMD(week?.start_date);
  const end = parseYMD(week?.end_date);

  if (start && end) {
    const inside = today >= start && today <= end;
    if (inside) {
      const dd = diffDaysLocal(today, start);
      if (dd >= 0 && dd <= 6) return dd;
    }
  }
  return today.getDay();
}

/* ----------------- ui helpers ----------------- */
function initialsOf(name) {
  const s = String(name || "").trim();
  if (!s) return "??";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "?";
  const b = parts[1]?.[0] || parts[0]?.[1] || "";
  return (a + b).toUpperCase();
}

function statusMeta(status) {
  const s = String(status || "PENDING").toUpperCase();

  // soft UI colors
  const map = {
    PENDING: { label: "PENDING", bg: "rgba(100,116,139,.10)", fg: "#334155", bd: "rgba(100,116,139,.18)" },
    SICK: { label: "SICK", bg: "rgba(239,68,68,.10)", fg: "#991b1b", bd: "rgba(239,68,68,.22)" },
    NO_CALL_NO_SHOW: { label: "NCNS", bg: "rgba(2,6,23,.10)", fg: "#0f172a", bd: "rgba(2,6,23,.18)" },
    LATE: { label: "LATE", bg: "rgba(245,158,11,.14)", fg: "#92400e", bd: "rgba(245,158,11,.22)" },
    EMERGENCY: { label: "EMERGENCY", bg: "rgba(59,130,246,.12)", fg: "#1d4ed8", bd: "rgba(59,130,246,.20)" },
    OTHER: { label: "OTHER", bg: "rgba(168,85,247,.12)", fg: "#6b21a8", bd: "rgba(168,85,247,.22)" },
  };

  return map[s] || map.PENDING;
}

// department accent colors (repeat)
function deptAccent(i) {
  const accents = [
    { bar: "#2563eb", head: "rgba(37,99,235,.10)" },
    { bar: "#22c55e", head: "rgba(34,197,94,.10)" },
    { bar: "#f59e0b", head: "rgba(245,158,11,.12)" },
    { bar: "#06b6d4", head: "rgba(6,182,212,.12)" },
    { bar: "#a855f7", head: "rgba(168,85,247,.12)" },
  ];
  return accents[i % accents.length];
}

/* ----------------- Modal ----------------- */
function Modal({ open, title, value, setValue, error, onClose, onSave, saving }) {
  if (!open) return null;

  return (
    <div className="att-modal-backdrop" role="dialog" aria-modal="true">
      <div className="att-modal">
        <div className="att-modal-head">
          <div className="att-modal-title">{title}</div>
          <button className="att-icon-btn" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="att-modal-body">
          <label className="att-label">Note</label>
          <textarea
            className="att-textarea"
            rows={4}
            placeholder="Type note here..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />

          {error ? <div className="att-error">{error}</div> : null}
        </div>

        <div className="att-modal-actions">
          <button className="att-btn ghost" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="att-btn" type="button" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

console.log("✅ AdminAttendanceTodayPage LOADED");

/* ----------------- Page ----------------- */
function AdminAttendanceTodayPage() {
  const { user, authFetch } = useAuth();


  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ date: "", rows: [] });

  const [searchText, setSearchText] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // ✅ modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("OTHER"); // OTHER | EDIT
  const [modalRow, setModalRow] = useState(null);
  const [modalText, setModalText] = useState("");
  const [modalError, setModalError] = useState("");

  async function readJsonSafe(res) {
    const ct = res.headers.get("content-type") || "";
    const text = await res.text();

    // if server returns HTML or plain text (like 502 page)
    if (!ct.includes("application/json")) {
      throw new Error(text?.slice(0, 120) || "Server returned non-JSON response");
    }

    // ✅ if empty json body
    if (!text || !text.trim()) return null;

    return JSON.parse(text);
  }

  function closeModal() {
    setModalOpen(false);
    setModalRow(null);
    setModalText("");
    setModalError("");
  }

  function openOtherModal(row) {
    setModalMode("OTHER");
    setModalRow(row);
    setModalText("");
    setModalError("");
    setModalOpen(true);
  }

  function openEditModal(row) {
    setModalMode("EDIT");
    setModalRow(row);
    setModalText(String(row?.attendance_note || row?.note || ""));
    setModalError("");
    setModalOpen(true);
  }

  async function fetchToday() {
    try {
      setErr("");
      setLoading(true);

      let week = null;
      try {
            const wRes = await authFetch("/api/weeks/current");
            if (wRes.ok) week = await readJsonSafe(wRes);
      } catch {
        // ignore
      }

          let path = "/api/attendance/today";

          if (week?.id) {
            const dayOfWeek = getTodayDayOfWeekForWeek(week);
            const qs = new URLSearchParams();
            qs.set("weekId", String(week.id));
            qs.set("dayOfWeek", String(dayOfWeek));
            path = `${path}?${qs.toString()}`;
          }

        const res = await authFetch(path);
        const json = await readJsonSafe(res);
      if (!res.ok) throw new Error(json?.error || "Failed to load attendance");

      setData({ date: json.date, rows: json.rows || [] });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function setAttendance(row, status, note = "") {
    try {
      setErr("");
      setSavingId(row.user_id);

      const res = await authFetch("/api/attendance/set", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: row.user_id,
          department_id: row.department_id ?? null,
          status,
          updated_by: user?.id || 1,
          note: note || "",
        }),
      });

      const json = await readJsonSafe(res);
      if (!res.ok) throw new Error(json?.error || "Failed to save attendance");

      await fetchToday();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSavingId(null);
    }
  }

  function handlePickStatus(row, statusKey) {
    if (statusKey === "OTHER") {
      openOtherModal(row);
      return;
    }
    return setAttendance(row, statusKey, "");
  }

  async function saveModal() {
    if (!modalRow) return;

    const note = String(modalText || "").trim();

    if (modalMode === "OTHER") {
      if (!note) {
        setModalError("Note is required for OTHER.");
        return;
      }
      await setAttendance(modalRow, "OTHER", note);
      closeModal();
      return;
    }

    // EDIT note mode:
    // keep current status (or PENDING)
    const current = String(modalRow?.attendance_status || "PENDING").toUpperCase();
    await setAttendance(modalRow, current, note);
    closeModal();
  }

  useEffect(() => {
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const departmentOptions = useMemo(() => {
    return Array.from(
      new Set((data.rows || []).map((r) => r.department_name || "No Department"))
    ).sort((a, b) => a.localeCompare(b));
  }, [data.rows]);

  const filteredRows = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();

    return (data.rows || []).filter((r) => {
      const userName = String(r.user_name || "").toLowerCase();
      const deptName = String(r.department_name || "No Department").toLowerCase();
      const noteText = String(r.attendance_note || r.note || "").toLowerCase();
      const currentStatus = String(r.attendance_status || "PENDING").toUpperCase();

      const matchesSearch =
        !q ||
        userName.includes(q) ||
        deptName.includes(q) ||
        noteText.includes(q);

      const matchesDepartment =
        departmentFilter === "all" ||
        String(r.department_name || "No Department") === String(departmentFilter);

      const matchesStatus =
        statusFilter === "all" ||
        currentStatus === String(statusFilter).toUpperCase();

      return matchesSearch && matchesDepartment && matchesStatus;
    });
  }, [data.rows, searchText, departmentFilter, statusFilter]);

  const groups = useMemo(() => {
    const map = {};
    for (const r of filteredRows || []) {
      const dept = r.department_name || "No Department";
      if (!map[dept]) map[dept] = [];
      map[dept].push(r);
    }
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredRows]);

  const modalTitle = useMemo(() => {
    if (!modalRow) return "Note";
    const who = modalRow?.user_name || "Staff";
    return modalMode === "OTHER" ? `Other — add note for ${who}` : `Edit note — ${who}`;
  }, [modalMode, modalRow]);

    function clearFilters() {
    setSearchText("");
    setDepartmentFilter("all");
    setStatusFilter("all");
  }

  const hasActiveFilters =
    searchText.trim() !== "" ||
    departmentFilter !== "all" ||
    statusFilter !== "all";

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* ✅ Styles (nice + colorful) */}
      <style>{`
        .att-wrap{ }

        .att-header{
          display:flex;
          justify-content:space-between;
          align-items:flex-end;
          gap: 12px;
          padding: 6px 4px;
        }

        .att-title{
          margin: 0;
          font-size: 28px;
          font-weight: 950;
          color: #0f172a;
        }

        .att-sub{
          opacity: .75;
          margin-top: 6px;
          font-size: 13px;
          color: #334155;
          font-weight: 800;
        }

        .att-top-btn{
          padding: 10px 14px;
          border-radius: 12px;
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          color: #0f172a;
          cursor: pointer;
          font-weight: 900;
          box-shadow: 0 10px 22px rgba(2,6,23,.06);
        }

                .att-filter-bar{
          margin-top: 16px;
          display:flex;
          flex-wrap:wrap;
          gap:12px;
          align-items:end;
        }

        .att-filter-item{
          display:flex;
          flex-direction:column;
          gap:6px;
          min-width:180px;
        }

        .att-filter-item label{
          font-size:11px;
          color:#64748b;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.04em;
        }

        .att-filter-item input,
        .att-filter-item select{
          height:44px;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding:0 14px;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          outline:none;
          font-size:13px;
          color:#0f172a;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: border-color .14s ease, box-shadow .14s ease, background .14s ease;
        }

        .att-filter-item input:hover,
        .att-filter-item select:hover{
          background:#fff;
          border-color:#cbd5e1;
        }

        .att-filter-item input:focus,
        .att-filter-item select:focus{
          border-color:#93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
          background:#fff;
        }

        .att-clear-btn{
          height:44px;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding:0 16px;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          color:#0f172a;
          font-size:13px;
          font-weight:900;
          cursor:pointer;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: transform .14s ease, filter .14s ease, background .14s ease, border-color .14s ease;
        }

        .att-clear-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.01);
          background:#fff;
          border-color:#cbd5e1;
        }

        .att-error{
          margin-top: 14px;
          padding: 12px;
          border-radius: 14px;
          background: rgba(239,68,68,.10);
          border: 1px solid rgba(239,68,68,.25);
          color: #991b1b;
          font-weight: 900;
          font-size: 13px;
        }

        .dept-card{
          position: relative;
          padding: 14px;
          border-radius: 18px;
          background: #fff;
          border: 1px solid rgba(2,6,23,.08);
          box-shadow: 0 10px 22px rgba(2,6,23,.06);
          overflow: hidden;
        }

        .dept-bar{
          position:absolute;
          left:0;
          top:0;
          bottom:0;
          width: 6px;
          background: var(--bar);
        }

        .dept-head{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 14px;
          background: var(--head);
          border: 1px solid rgba(2,6,23,.06);
        }

        .dept-name{
          font-size: 16px;
          font-weight: 950;
          color: #0f172a;
        }

        .dept-meta{
          display:flex;
          align-items:center;
          gap: 10px;
          opacity: .85;
          font-size: 12px;
          color: #334155;
          font-weight: 800;
        }

        .att-icon-btn{
          width: 36px;
          height: 36px;
          border-radius: 12px;
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          cursor: pointer;
          font-weight: 950;
          box-shadow: 0 10px 22px rgba(2,6,23,.06);
          display:inline-flex;
          align-items:center;
          justify-content:center;
        }

        .staff-list{
          margin-top: 12px;
          display:grid;
          gap: 10px;
        }

        .staff-row{
          display:grid;
          grid-template-columns: 1.2fr 2fr;
          gap: 10px;
          align-items:center;
          padding: 12px;
          border-radius: 16px;
          background: rgba(15,23,42,.03);
          border: 1px solid rgba(2,6,23,.08);
        }

        .staff-left{
          display:flex;
          align-items:center;
          gap: 12px;
          min-width:0;
        }

        .avatar{
          width: 34px;
          height: 34px;
          border-radius: 999px;
          background: rgba(37,99,235,.12);
          border: 1px solid rgba(37,99,235,.18);
          color: #1d4ed8;
          font-weight: 950;
          display:flex;
          align-items:center;
          justify-content:center;
          flex: 0 0 auto;
        }

        .staff-name{
          font-weight: 950;
          font-size: 14px;
          color: #0f172a;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }

        .staff-sub{
          opacity: .85;
          font-size: 12px;
          margin-top: 4px;
          color: #334155;
          font-weight: 800;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }

        .badge{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 950;
          border: 1px solid var(--bd);
          background: var(--bg);
          color: var(--fg);
          margin-left: 8px;
        }

        .right-actions{
          display:flex;
          gap: 8px;
          flex-wrap:wrap;
          justify-content:flex-end;
          align-items:center;
        }

        .pill{
          padding: 8px 10px;
          border-radius: 12px;
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          color: #0f172a;
          cursor: pointer;
          font-size: 12px;
          font-weight: 900;
          white-space:nowrap;
          transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
        }
        .pill:hover{ transform: translateY(-1px); box-shadow: 0 10px 18px rgba(2,6,23,.08); }
        .pill.active{
          background: rgba(37,99,235,.12);
          color: #1d4ed8;
          border: 1px solid rgba(37,99,235,.22);
          box-shadow: 0 10px 18px rgba(37,99,235,.10);
        }
        .pill:disabled{ opacity: .55; cursor:not-allowed; transform:none; box-shadow:none; }

        /* ---------------- Modal ---------------- */
        .att-modal-backdrop{
          position: fixed;
          inset: 0;
          background: rgba(2,6,23,.45);
          display:flex;
          align-items:center;
          justify-content:center;
          z-index: 9999;
          padding: 18px;
        }

        .att-modal{
          width: min(520px, 100%);
          background: #fff;
          border-radius: 18px;
          border: 1px solid rgba(2,6,23,.10);
          box-shadow: 0 22px 50px rgba(2,6,23,.25);
          overflow:hidden;
        }

        .att-modal-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap: 12px;
          padding: 14px 14px;
          background: rgba(37,99,235,.08);
          border-bottom: 1px solid rgba(2,6,23,.08);
        }

        .att-modal-title{
          font-weight: 950;
          color: #0f172a;
          font-size: 14px;
        }

        .att-modal-body{
          padding: 14px;
        }

        .att-label{
          display:block;
          font-size: 12px;
          font-weight: 950;
          color: #334155;
          margin-bottom: 8px;
        }

        .att-textarea{
          width: 100%;
          border-radius: 14px;
          border: 1px solid rgba(2,6,23,.10);
          padding: 12px;
          font-size: 13px;
          font-weight: 800;
          color: #0f172a;
          outline: none;
          resize: vertical;
        }
          .att-textarea:focus{
            border-color: rgba(37,99,235,.35);
            box-shadow: 0 0 0 4px rgba(37,99,235,.12);
          }

        .att-modal-actions{
          display:flex;
          justify-content:flex-end;
          gap: 10px;
          padding: 12px 14px 14px;
          border-top: 1px solid rgba(2,6,23,.08);
        }

        .att-btn{
          padding: 10px 14px;
          border-radius: 12px;
          border: 1px solid rgba(37,99,235,.18);
          background: rgba(37,99,235,.12);
          color: #1d4ed8;
          cursor:pointer;
          font-weight: 950;
        }
        .att-btn.ghost{
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          color: #0f172a;
        }
        .att-btn:disabled{ opacity:.6; cursor:not-allowed; }
      `}</style>

      {/* Header */}
      <div className="att-header">
        <div>
          <h2 className="att-title">Today&apos;s Attendance</h2>
          <div className="att-sub">
            Date: <b>{data.date || "—"}</b>
          </div>
        </div>

        <button className="att-top-btn" onClick={fetchToday}>
          Refresh
        </button>
      </div>

      {err ? <div className="att-error">{err}</div> : null}

            <div className="att-filter-bar">
        <div className="att-filter-item" style={{ flex: "1 1 260px" }}>
          <label>Search Staff</label>
          <input
            type="text"
            placeholder="Search name, department, note..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="att-filter-item">
          <label>Department</label>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
          >
            <option value="all">All Departments</option>
            {departmentOptions.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        <div className="att-filter-item">
          <label>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="PENDING">Pending</option>
            {STATUS_LIST.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {hasActiveFilters && (
          <div className="att-filter-item" style={{ minWidth: "150px" }}>
            <label>&nbsp;</label>
            <button
              type="button"
              className="att-clear-btn"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ marginTop: 18, opacity: 0.75, color: "#334155", fontWeight: 800 }}>Loading...</div>
      ) : (
        <div style={{ marginTop: 18, display: "grid", gap: 16 }}>
          {groups.length === 0 ? (
            <div style={{ opacity: 0.75, color: "#334155", fontWeight: 800 }}>No staff found for today.</div>
          ) : (
            groups.map(([deptName, rows], gi) => {
              const acc = deptAccent(gi);

              return (
                <div
                  key={deptName}
                  className="dept-card"
                  style={{
                    ["--bar"]: acc.bar,
                    ["--head"]: acc.head,
                  }}
                >
                  <div className="dept-bar" />

                  <div className="dept-head">
                    <div className="dept-name">{deptName}</div>

                    <div className="dept-meta">
                      Team: <b>{rows.length}</b>
                      <button
                        type="button"
                        className="att-icon-btn"
                        title="Department actions (later)"
                        onClick={() => alert("Department actions later")}
                      >
                        ✎
                      </button>
                    </div>
                  </div>

                  <div className="staff-list">
                    {rows.map((r) => {
                      const current = String(r.attendance_status || "PENDING").toUpperCase();
                      const noteText = String(r.attendance_note || r.note || "").trim();
                      const isSaving = savingId === r.user_id;
                      const meta = statusMeta(current);

                      return (
                        <div key={r.user_id} className="staff-row">
                          <div className="staff-left">
                            <div className="avatar" title={r.user_name}>
                              {initialsOf(r.user_name)}
                            </div>

                            <div style={{ minWidth: 0 }}>
                              <div className="staff-name">{r.user_name}</div>

                              <div className="staff-sub" title={noteText || ""}>
                                <span style={{ fontWeight: 900 }}>Status:</span>{" "}
                                <span
                                  className="badge"
                                  style={{
                                    ["--bg"]: meta.bg,
                                    ["--fg"]: meta.fg,
                                    ["--bd"]: meta.bd,
                                  }}
                                >
                                  {meta.label}
                                </span>
                                {noteText ? (
                                  <span style={{ marginLeft: 8 }}>
                                    • Note: <b>{noteText}</b>
                                  </span>
                                ) : null}
                                {isSaving ? <span style={{ marginLeft: 8 }}> (saving...)</span> : null}
                              </div>
                            </div>
                          </div>

                          <div className="right-actions">
                            {/* ✅ Pencil = edit note */}
                            <button
                              type="button"
                              className="att-icon-btn"
                              disabled={isSaving}
                              title="Add/Edit note"
                              onClick={() => openEditModal(r)}
                            >
                              ✎
                            </button>

                            {STATUS_LIST.map((s) => (
                              <button
                                key={s.key}
                                type="button"
                                className={`pill ${current === s.key ? "active" : ""}`}
                                disabled={isSaving}
                                onClick={() => handlePickStatus(r, s.key)}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ✅ Proper modal for Other + Edit */}
      <Modal
        open={modalOpen}
        title={modalTitle}
        value={modalText}
        setValue={setModalText}
        error={modalError}
        saving={Boolean(savingId) && modalRow?.user_id === savingId}
        onClose={closeModal}
        onSave={saveModal}
      />
    </div>
  );
}

export default AdminAttendanceTodayPage;
